import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { useRecordContext, RaRecord } from 'react-admin';
import { Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard, fmtCalendarDate, fmtDate } from '../../shared/Panel';
import { validityEnded } from '../../shared/dateTime';
import { SOON_DAYS, daysUntil, sortByTerm, type VehicleEligibilityRow } from './eligibilityTerms';
import { CorrectionButton, CorrectionMark, EligibilityTermDialog, useFleetCorrections } from './CarCorrections';
import { correctionFor, correctionsOf } from './fleetCorrectionsApi';

export { SOON_DAYS, daysUntil, sortByTerm, type VehicleEligibilityRow };

/**
 * „ważne / nieważne / brak danych” — odpowiedź Marathonu 16.10.2026, pyt. 8:
 * `true` = ważne (termin później niż teraz), `false` = nieważne. Do 16.10
 * status stał dosłownie („tak / nie”), bo jego znaczenia nie znaliśmy. Bez
 * przedrostka „status:” (poprawa brzmienia 01.10.2026) — kolumna i tak
 * nazywa się „Status z TMS”.
 */
export const statusLabel = (s: boolean | null | undefined): string =>
  s === true ? 'ważne' : s === false ? 'nieważne' : 'brak danych';

/**
 * Czy termin-DATA jest wcześniejszy niż dziś (dzień polski): wpis z terminem
 * przed dzisiaj nie obowiązuje. Ta sama reguła co u kierowcy
 * (`validityEnded`, 08.10.2026): „do 12.09” obowiązuje przez cały 12.09.
 */
export function expiredBeforeToday(iso: string | null | undefined, now: Date = new Date()): boolean {
  return validityEnded(iso, now);
}

/** Typ to wartość z TMS klienta — pokazujemy ją, kolor tylko podpowiada. */
const TypeChip = ({ type }: { type: string | null }) => (
  <Chip
    size="small"
    variant="outlined"
    color={type === 'Blokada' ? 'error' : type === 'Alert' ? 'warning' : 'default'}
    label={type ?? 'bez typu'}
  />
);

/** „za 12 dni”, „dziś”, „jutro”. */
function leftText(days: number): string {
  if (days === 0) return 'dziś';
  if (days === 1) return 'jutro';
  return `za ${days} dni`;
}

/**
 * „Uprawnienia i alerty z kartoteki TMS” na karcie auta.
 *
 * Endpoint klienta działa od 01.10.2026 (wcześniej 500). Pokazujemy typ wg
 * klasyfikacji klienta, status słownie („ważne / nieważne” — odpowiedź
 * Marathonu 16.10.2026, pyt. 8) i termin.
 *
 * Od 07.10.2026 (prototyp p. Mariusza, zmiana 16): wpisy od najbliższego
 * terminu, terminy w ciągu 30 dni i wygasłe wyróżnione z liczbą dni, reszta
 * zwinięta pod przyciskiem. Od 16.10.2026 wpis nieważny według TMS jest
 * wyróżniony i zawsze widoczny, jak wygasły. Podpowiedzi aut: nieważna
 * Blokada wyklucza, Alert ostrzega (`via/vehicle-eligibilities.ts`).
 */
export const CarEligibilities = ({ now }: { now?: Date }) => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const [showAll, setShowAll] = useState(false);
  // Korekty terminów (22.10.2026, `fleetCorrections`): „Zmień termin”,
  // „≠ TMS” i „przywróć z TMS” — wyłączony przełącznik: tabela jak dotąd.
  const fixes = useFleetCorrections();
  const [editing, setEditing] = useState<VehicleEligibilityRow | null>(null);
  if (!r) return null;
  const corrections = correctionsOf(r);
  const editable = fixes.enabled;
  const refusal = !fixes.context
    ? 'Sprawdzam, czy możesz korygować kartotekę tego pojazdu…'
    : fixes.context.access.allowed
      ? null
      : (fixes.context.access.refusal ?? 'Brak uprawnień do korekty.');
  const rows: VehicleEligibilityRow[] = Array.isArray(r.eligibilities)
    ? sortByTerm(r.eligibilities as VehicleEligibilityRow[])
    : [];
  const imported = rows
    .map((e) => e.importedAt)
    .filter((d): d is string => typeof d === 'string')
    .sort()
    .pop();
  // Nieważny według TMS (pyt. 8, 16.10.2026) — zawsze widoczny, jak wygasły.
  const soon = (e: VehicleEligibilityRow) => {
    if (e.status === false) return true;
    // Termin skorygowany w aplikacji — zawsze widoczny, ze znacznikiem „≠ TMS”.
    if (editable && e.correctionId !== undefined) return true;
    const d = daysUntil(e.validUpTo, now);
    return d !== null && d <= SOON_DAYS;
  };
  const rest = rows.filter((e) => !soon(e)).length;
  const shown = rows.filter((e) => showAll || soon(e));
  return (
    <SectionCard title="Uprawnienia i alerty z kartoteki TMS">
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          TMS nie podaje uprawnień ani alertów dla tego pojazdu albo dane nie zostały jeszcze pobrane.
        </Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nazwa</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Ważne do</TableCell>
                <TableCell>Status z TMS</TableCell>
                {editable ? <TableCell aria-label="Korekta" /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={editable ? 5 : 4} sx={{ color: 'text.secondary' }}>
                    Brak terminów w ciągu {SOON_DAYS} dni.
                  </TableCell>
                </TableRow>
              ) : null}
              {shown.map((e) => {
                const expired = expiredBeforeToday(e.validUpTo, now);
                const days = daysUntil(e.validUpTo, now);
                const near = !expired && days !== null && days <= SOON_DAYS;
                const marked = expired || near || e.status === false;
                const fix =
                  editable && e.eligibilityId !== undefined
                    ? correctionFor(corrections, 'eligibility', e.eligibilityId)
                    : null;
                return (
                  <TableRow
                    key={e.name}
                    data-soon={marked ? 'true' : undefined}
                    sx={marked ? { '& td': { bgcolor: (t) => alpha(t.palette.warning.main, 0.1) } } : undefined}
                  >
                    <TableCell>{e.name}</TableCell>
                    <TableCell>
                      <TypeChip type={e.type} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>{fmtCalendarDate(e.validUpTo)}</span>
                        {expired && <Chip size="small" color="error" label="wygasło" />}
                        {near && days !== null && (
                          <Chip size="small" color="warning" variant="outlined" label={leftText(days)} />
                        )}
                        {fix ? <CorrectionMark correction={fix} testKey={`elig-${e.eligibilityId}`} /> : null}
                      </Stack>
                      {fix?.warning ? (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                          {fix.warning}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={e.status === false ? { color: 'error.main', fontWeight: 600 } : undefined}>
                      {fix ? (
                        // Status z TMS dotyczy terminu z TMS — przy korekcie liczy się sam termin.
                        <>
                          termin z korekty
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            TMS: {statusLabel(e.tmsStatus)}
                          </Typography>
                        </>
                      ) : (
                        statusLabel(e.status)
                      )}
                    </TableCell>
                    {editable ? (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {e.eligibilityId !== undefined ? (
                          <CorrectionButton
                            label="Zmień termin"
                            testId={`car-eligibility-edit-${e.eligibilityId}`}
                            refusal={refusal}
                            onClick={() => setEditing(e)}
                          />
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {rest > 0 ? (
            <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 0.5 }}>
              {showAll ? 'Ukryj dalsze terminy' : `Pokaż pozostałe (${rest})`}
            </Button>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Status z TMS: „ważne” albo „nieważne”. Wyróżniamy wpisy nieważne i terminy przypadające w ciągu{' '}
            {SOON_DAYS} dni; „wygasło” oznacza termin wcześniejszy niż dzisiaj.
            {imported ? ` Pobrano z TMS: ${fmtDate(imported)}.` : ''}
          </Typography>
        </>
      )}
      {editable && editing && editing.eligibilityId !== undefined ? (
        <EligibilityTermDialog
          open
          onClose={() => setEditing(null)}
          eligibilityId={editing.eligibilityId}
          name={editing.name}
          current={editing.validUpTo}
          tms={editing.correctionId !== undefined ? (editing.tmsValidUpTo ?? null) : editing.validUpTo}
        />
      ) : null}
    </SectionCard>
  );
};
